import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import Button from '../Button';
import Notification from '../Notification';
import {
  dueStudioOfflineMessage,
  isInsideDueStudio,
  openProjectInsideDueStudio,
  sendProjectToDueStudio4,
  sendProjectToDueStudio5,
} from '../../utils/dueStudio';

import { Container, Menu, MenuItem, MenuTitle } from './styles';

const propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  projectName: PropTypes.string,
  token: PropTypes.string,
  text: PropTypes.string,
  color: PropTypes.string,
  minWidth: PropTypes.number,
};

const defaultProps = {
  projectName: '',
  token: '',
  text: 'Cortar projeto',
  color: 'var(--accent)',
  minWidth: 0,
};

/**
 * Botão "Cortar projeto".
 *
 * Dentro do Due Studio 5 (aba "Projetos") o clique manda o projeto direto para a área de
 * trabalho — já se sabe qual app está aberto. No navegador, o mesmo botão abre uma escolha
 * entre o Due Studio 5 e o 4, porque só o usuário sabe qual dos dois tem instalado.
 */
const CutProjectButton = ({
  projectId,
  projectName,
  token,
  text,
  color,
  minWidth,
}) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    function handleClickOutside(event) {
      if (!containerRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  async function sendToDueStudio(send) {
    setMenuOpen(false);

    Notification({
      message: 'Iniciando comunicação com Due Studio',
      type: Notification.types.INFO,
    });

    try {
      const message = await send();

      Notification({
        message: message || 'Projeto enviado com sucesso',
        type: Notification.types.INFO,
      });
    } catch (err) {
      Notification({
        message: dueStudioOfflineMessage,
        type: Notification.types.ERROR,
        timeout: 10000,
      });
    }
  }

  function handleButtonClick() {
    if (isInsideDueStudio()) {
      openProjectInsideDueStudio({ projectId, projectName });
      return;
    }

    setMenuOpen(open => !open);
  }

  function handleSelectDueStudio5() {
    return sendToDueStudio(() =>
      sendProjectToDueStudio5({ projectId, projectName, token })
    );
  }

  function handleSelectDueStudio4() {
    return sendToDueStudio(() => sendProjectToDueStudio4({ projectId, token }));
  }

  function renderMenuItem(label, onSelect) {
    return (
      <MenuItem
        role="menuitem"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect();
          }
        }}
      >
        {label}
      </MenuItem>
    );
  }

  return (
    <Container ref={containerRef}>
      <Button
        text={text}
        primary
        color={color}
        minWidth={minWidth}
        onClick={handleButtonClick}
      />

      {isMenuOpen && (
        <Menu role="menu">
          <MenuTitle>Abrir com</MenuTitle>
          {renderMenuItem('Due Studio 5', handleSelectDueStudio5)}
          {renderMenuItem('Due Studio 4', handleSelectDueStudio4)}
        </Menu>
      )}
    </Container>
  );
};

CutProjectButton.propTypes = propTypes;
CutProjectButton.defaultProps = defaultProps;

export default CutProjectButton;
